<script setup lang="ts">
import WrButton from "~/components/global/WrButton.vue";

interface FaqItem {
  title: string;
  description: string;
}

const emit = defineEmits<{ "cta-clicked": [] }>();

withDefaults(
  defineProps<{
    heading: string;
    subcopy: string;
    items: FaqItem[];
    eyebrow?: string;
    ctaText?: string;
    ctaLink?: string | null;
    /** Accent hue: "purple" (default, Flutter page) or "blue" (VaahAgents look). */
    variant?: "purple" | "blue";
    /** Toggle icon: "plus" (default) or "chevron". */
    icon?: "plus" | "chevron";
  }>(),
  {
    eyebrow: "FAQ",
    ctaText: "Talk to our Flutter experts",
    ctaLink: null,
    variant: "purple",
    icon: "plus",
  },
);

const openIndex = ref<number | null>(0);
const toggle = (i: number) => {
  openIndex.value = openIndex.value === i ? null : i;
};
</script>

<template>
  <section class="relative" :class="variant === 'blue' ? 'fl-faq-blue' : ''">
    <div class="text-center">
      <span class="fl-eyebrow"><span class="fl-eyebrow-dot" />{{ eyebrow }}</span>
      <h2 class="fl-heading">{{ heading }}</h2>
      <p class="fl-subcopy text-black opacity-70 dark:text-white">{{ subcopy }}</p>
    </div>

    <div class="mt-10 lg:mt-14 px-4 max-w-[820px] mx-auto space-y-4">
      <div
        v-for="(item, i) in items"
        :key="item.title"
        class="fl-item"
        :class="openIndex === i ? 'fl-item-open' : ''"
      >
        <button
          type="button"
          class="w-full flex items-center justify-between gap-4 text-left px-5 lg:px-6 py-4 lg:py-5"
          :aria-expanded="openIndex === i"
          :aria-controls="'fl-faq-panel-' + i"
          @click="toggle(i)"
        >
          <span class="fl-item-title" :class="openIndex === i ? 'fl-item-title-open' : ''">{{ item.title }}</span>
          <span
            class="fl-item-toggle"
            :class="[icon === 'chevron' ? 'is-chevron' : '', openIndex === i ? 'fl-item-toggle-open' : '']"
          >
            <svg v-if="icon === 'plus'" viewBox="0 0 24 24" class="size-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            <svg v-else viewBox="0 0 24 24" class="size-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </span>
        </button>
        <div
          :id="'fl-faq-panel-' + i"
          role="region"
          :aria-hidden="openIndex !== i"
          class="grid transition-all duration-300 ease-out"
          :class="openIndex === i ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'"
        >
          <div class="overflow-hidden">
            <p class="px-5 lg:px-6 pb-5 text-[14px] lg:text-[15px] leading-relaxed text-[#555] dark:text-[#BCB2C3]" >{{ item.description }}</p>
          </div>
        </div>
      </div>
    </div>

    <div class="flex justify-center mt-10 lg:mt-12">
      <WrButton
        :link="ctaLink || undefined"
        @click="!ctaLink ? emit('cta-clicked') : undefined"
        container-class="h-[44px] lg:h-[48px]"
        append-class="text-sm lg:font-normal px-5 lg:px-6"
      >
        {{ ctaText }}
      </WrButton>
    </div>
  </section>
</template>

<style scoped>
.fl-eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.2em;
  color: #887fff; margin-bottom: 14px;
}
.fl-eyebrow-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: #ba55fe; box-shadow: 0 0 8px rgba(186, 85, 254, 0.8);
  animation: fl-blink 2.4s ease-in-out infinite;
}
@keyframes fl-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
@media (min-width: 1024px) { .fl-eyebrow { font-size: 12px; } }

.fl-heading {
  font-family: "ITC Avant Garde Gothic Demi", "ITC Avant Garde Gothic", "AvantGarde", sans-serif;
  font-size: clamp(28px, 4vw, 48px); font-weight: 600; line-height: 1.1; text-align: center;
  background: linear-gradient(92deg, #12dbff -3.57%, #ba55fe 80.72%);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; color: transparent;
}
.fl-subcopy {
  font-family: "ITC Avant Garde Gothic", "AvantGarde", sans-serif;
  font-size: 16px; line-height: 1.6; letter-spacing: 0.36px; margin-top: 16px;
  text-align: center; max-width: 720px; margin-left: auto; margin-right: auto; padding: 0 16px;
}
@media (min-width: 1024px) { .fl-subcopy { font-size: 18px; } }

.fl-item {
  border-radius: 16px; border: 1px solid rgba(136, 127, 255, 0.15);
  background: rgba(18, 0, 44, 0.35);
  transition: border-color 0.3s ease, background 0.3s ease, box-shadow 0.3s ease;
}
.fl-item:hover {
  border-color: rgba(136, 127, 255, 0.35);
  background: rgba(18, 0, 44, 0.5);
}
.fl-item-open {
  border-color: rgba(186, 85, 254, 0.4);
  background: rgba(35, 27, 255, 0.08);
  box-shadow: 0 18px 40px -24px rgba(186, 85, 254, 0.4);
}

.fl-item-title { font-size: 15px; font-weight: 600; color: #c2c6d4; transition: color 0.3s ease; }
@media (min-width: 1024px) { .fl-item-title { font-size: 17px; } }
.fl-item:hover .fl-item-title,
.fl-item-title-open { color: #fff; }

.fl-item-toggle {
  width: 28px; height: 28px; flex-shrink: 0; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  border: 1px solid rgba(136, 127, 255, 0.3); color: #ba55fe;
  transition: transform 0.3s ease, border-color 0.3s ease, background 0.3s ease, box-shadow 0.3s ease;
}
.fl-item:hover .fl-item-toggle { border-color: rgba(186, 85, 254, 0.5); }
.fl-item-toggle-open {
  transform: rotate(45deg);
  border-color: rgba(186, 85, 254, 0.5);
  background: rgba(186, 85, 254, 0.15);
  box-shadow: 0 0 14px rgba(186, 85, 254, 0.35);
}
/* Chevron icon flips instead of rotating into an "x" */
.fl-item-toggle.is-chevron.fl-item-toggle-open { transform: rotate(180deg); }

/* Light theme */
html:not(.my-app-dark) .fl-eyebrow { color: #613faa; }
html:not(.my-app-dark) .fl-item {
  background: #fff;
  border-color: rgba(208, 208, 208, 0.5);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}
html:not(.my-app-dark) .fl-item:hover { background: #fff; border-color: rgba(136, 127, 255, 0.4); }
html:not(.my-app-dark) .fl-item-open {
  background: #fbf8ff;
  border-color: rgba(186, 85, 254, 0.4);
  box-shadow: 0 8px 24px rgba(166, 85, 246, 0.12);
}
html:not(.my-app-dark) .fl-item-title { color: #333; }
html:not(.my-app-dark) .fl-item:hover .fl-item-title,
html:not(.my-app-dark) .fl-item-title-open { color: #613faa; }
html:not(.my-app-dark) .fl-item-toggle { border-color: rgba(136, 127, 255, 0.4); color: #7c3aed; }
html:not(.my-app-dark) .fl-item:hover .fl-item-toggle { border-color: rgba(186, 85, 254, 0.5); }
html:not(.my-app-dark) .fl-item-toggle-open {
  border-color: rgba(186, 85, 254, 0.5);
  background: rgba(186, 85, 254, 0.1);
  box-shadow: 0 0 12px rgba(166, 85, 246, 0.2);
}

/* Blue accent variant (VaahAgents page) — same layout, blue hues */
.fl-faq-blue .fl-eyebrow { color: #6d8bff; }
.fl-faq-blue .fl-item { border-color: rgba(109, 139, 255, 0.15); }
.fl-faq-blue .fl-item:hover { border-color: rgba(109, 139, 255, 0.35); }
.fl-faq-blue .fl-item-open {
  border-color: rgba(77, 141, 255, 0.4);
  box-shadow: 0 18px 40px -24px rgba(77, 141, 255, 0.4);
}
.fl-faq-blue .fl-item-toggle { border-color: rgba(109, 139, 255, 0.3); color: #4d8dff; }
.fl-faq-blue .fl-item:hover .fl-item-toggle { border-color: rgba(77, 141, 255, 0.5); }
.fl-faq-blue .fl-item-toggle-open {
  border-color: rgba(77, 141, 255, 0.5);
  background: rgba(77, 141, 255, 0.15);
  box-shadow: 0 0 14px rgba(77, 141, 255, 0.35);
}

/* Blue variant — light theme */
html:not(.my-app-dark) .fl-faq-blue .fl-eyebrow { color: #501d71; }
html:not(.my-app-dark) .fl-faq-blue .fl-item:hover { border-color: rgba(148, 103, 192, 0.4); }
html:not(.my-app-dark) .fl-faq-blue .fl-item-open {
  background: #faf5ff;
  border-color: rgba(148, 103, 192, 0.4);
  box-shadow: 0 8px 24px rgba(148, 103, 192, 0.12);
}
html:not(.my-app-dark) .fl-faq-blue .fl-item-title:hover,
html:not(.my-app-dark) .fl-faq-blue .fl-item-title-open { color: #501d71; }
html:not(.my-app-dark) .fl-faq-blue .fl-item-toggle { border-color: rgba(148, 103, 192, 0.4); color: #6d28d9; }
html:not(.my-app-dark) .fl-faq-blue .fl-item:hover .fl-item-toggle { border-color: rgba(148, 103, 192, 0.5); }
html:not(.my-app-dark) .fl-faq-blue .fl-item-toggle-open {
  border-color: rgba(148, 103, 192, 0.5);
  background: rgba(148, 103, 192, 0.1);
  box-shadow: 0 0 12px rgba(148, 103, 192, 0.2);
}

@media (prefers-reduced-motion: reduce) {
  .fl-eyebrow-dot { animation: none; }
}
</style>
